import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../store/rootReducer";
import { Property, StratifyDocument } from "@stratify/shared";
import { selectDocuments } from "../../documents/store/documentsSelectors";

export const selectPropertiesState = (state: RootState) => state.properties;

export const selectProperties = createSelector(
  [selectPropertiesState],
  (state) => state.properties
);

export const selectPropertyById = createSelector(
  [selectProperties, (_state: RootState, id: string) => id],
  (properties, id) => properties.find((property) => property.id === id)
);

export const selectPropertiesLoading = createSelector(
  [selectPropertiesState],
  (state) => state.loading
);

export const selectPropertiesError = createSelector(
  [selectPropertiesState],
  (state) => state.error
);

export const selectPropertiesFilters = createSelector(
  [selectPropertiesState],
  (state) => state.filters
);

export const selectPropertyDocumentListUi = createSelector(
  [selectPropertiesState],
  (state) => state.documentListUi
);

const sortProperties = (
  properties: Property[],
  sortBy: string,
  sortOrder: "asc" | "desc"
): Property[] => {
  const order = sortOrder === "asc" ? 1 : -1;

  return [...properties].sort((a, b) => {
    switch (sortBy) {
      case "address":
        return order * (a.address || "").localeCompare(b.address || "");
      case "neighbourhood":
        return order * (a.neighbourhood || "").localeCompare(b.neighbourhood || "");
      case "suburb":
        return order * (a.suburb || "").localeCompare(b.suburb || "");
      case "city":
        return order * (a.city || "").localeCompare(b.city || "");
      case "strataPlanNumber":
        return (
          order *
          (a.strataPlanNumber || "").localeCompare(b.strataPlanNumber || "")
        );
      case "createdAt":
        return (
          order *
          (a.createdAt.toDate().getTime() - b.createdAt.toDate().getTime())
        );
      default:
        return 0;
    }
  });
};

const sortPropertyDocuments = (
  documents: StratifyDocument[],
  sortBy: string,
  sortOrder: "asc" | "desc"
): StratifyDocument[] => {
  const order = sortOrder === "asc" ? 1 : -1;

  return [...documents].sort((a, b) => {
    switch (sortBy) {
      case "filename":
        return order * a.filename.localeCompare(b.filename);
      case "type":
        return order * a.type.localeCompare(b.type);
      case "size":
        return order * (a.size - b.size);
      case "metadata.category":
        return (
          order *
          (a.metadata.category?.name || "").localeCompare(
            b.metadata.category?.name || ""
          )
        );
      case "metadata.processing.status":
        return (
          order *
          a.metadata.processing.status.localeCompare(
            b.metadata.processing.status
          )
        );
      case "createdAt":
        return (
          order *
          (a.createdAt.toDate().getTime() - b.createdAt.toDate().getTime())
        );
      default:
        return 0;
    }
  });
};

export const selectFilteredProperties = createSelector(
  [selectProperties, selectPropertiesFilters],
  (properties, filters): Property[] => {
    let filtered = properties;

    // Apply individual field filters
    if (filters.neighbourhood) {
      const neighbourhoodLower = filters.neighbourhood.toLowerCase();
      filtered = filtered.filter(
        (property: Property) =>
          property.neighbourhood?.toLowerCase().includes(neighbourhoodLower)
      );
    }

    if (filters.suburb) {
      const suburbLower = filters.suburb.toLowerCase();
      filtered = filtered.filter(
        (property: Property) =>
          property.suburb?.toLowerCase().includes(suburbLower)
      );
    }

    if (filters.city) {
      const cityLower = filters.city.toLowerCase();
      filtered = filtered.filter(
        (property: Property) =>
          property.city?.toLowerCase().includes(cityLower)
      );
    }

    // Apply general search filter (still works across all fields)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (property: Property) =>
          property.address?.toLowerCase().includes(searchLower) ||
          property.neighbourhood?.toLowerCase().includes(searchLower) ||
          property.suburb?.toLowerCase().includes(searchLower) ||
          property.city?.toLowerCase().includes(searchLower) ||
          property.strataPlanNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    return sortProperties(filtered, filters.sortBy, filters.sortOrder);
  }
);

export const selectDocumentCountsByPropertyId = createSelector(
  [selectDocuments, selectProperties],
  (documents: StratifyDocument[], properties: Property[]) => {
    const entries = properties.map((property) => {
      const propertyDocs = documents.filter(
        (doc) =>
          (doc.metadata.address &&
            property.address &&
            doc.metadata.address === property.address) ||
          (doc.metadata.strataPlanNumber &&
            property.strataPlanNumber &&
            doc.metadata.strataPlanNumber === property.strataPlanNumber)
      );
      return [property.id, propertyDocs.length];
    });
    return Object.fromEntries(entries) as Record<string, number>;
  }
);

export const selectPropertyDocuments = createSelector(
  [
    selectDocuments,
    selectPropertyDocumentListUi,
    (
      _state: RootState,
      property: { address?: string; strataPlanNumber?: string }
    ) => property,
  ],
  (documents, documentListUi, property) => {
    if (!property) return documents;

    // Filter documents that match either the address or strata plan
    const filtered = documents.filter((doc: StratifyDocument) => {
      const metadata = doc.metadata;
      return (
        (metadata.address &&
          property.address &&
          metadata.address === property.address) ||
        (metadata.strataPlanNumber &&
          property.strataPlanNumber &&
          metadata.strataPlanNumber === property.strataPlanNumber)
      );
    });

    // Apply sorting
    return sortPropertyDocuments(
      filtered,
      documentListUi.sortBy,
      documentListUi.sortOrder
    );
  }
);

export const selectUniqueNeighbourhoods = createSelector(
  [selectProperties],
  (properties) => {
    const neighbourhoods = new Set<string>();
    properties.forEach((property) => {
      if (property.neighbourhood && property.neighbourhood.trim()) {
        neighbourhoods.add(property.neighbourhood.trim());
      }
    });
    return Array.from(neighbourhoods).sort();
  }
);

export const selectUniqueSuburbs = createSelector(
  [selectProperties],
  (properties) => {
    const suburbs = new Set<string>();
    properties.forEach((property) => {
      if (property.suburb && property.suburb.trim()) {
        suburbs.add(property.suburb.trim());
      }
    });
    return Array.from(suburbs).sort();
  }
);

export const selectUniqueCities = createSelector(
  [selectProperties],
  (properties) => {
    const cities = new Set<string>();
    properties.forEach((property) => {
      if (property.city && property.city.trim()) {
        cities.add(property.city.trim());
      }
    });
    return Array.from(cities).sort();
  }
);
